/*
 * Copyright © HatioLab Inc. All rights reserved.
 */

import {
  Component,
  ComponentNature,
  RealObjectGroup,
  RealObject,
  RectPath,
  Shape,
  ValueHolder,
  sceneComponent
} from '@hatiolab/things-scene'
import * as THREE from 'three'
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js'

const NATURE: ComponentNature = {
  mutable: false,
  resizable: true,
  rotatable: true,
  properties: [
    {
      type: 'number',
      label: 'value',
      name: 'value',
      property: 'value'
    },
    {
      type: 'checkbox',
      label: 'blink',
      name: 'blink',
      property: 'blink'
    }
  ],
  help: 'scene/component/signal-tower'
}

/* ── 색상 상수 ── */
const LIGHT_COLORS = {
  red: { on: '#cc0000', off: '#1a0505' },
  yellow: { on: '#cc8800', off: '#1a1400' },
  green: { on: '#00bb00', off: '#051a05' }
} as const

const BODY_COLOR = '#333333'
const POLE_COLOR = '#888888'
const RING_COLOR = '#222222'

/* ── 2D 렌더 ── */

const BLINK_PERIOD = 500 // ms

function isBlinkOn() {
  return Math.floor(Date.now() / BLINK_PERIOD) % 2 === 0
}

function renderSignalTower(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, value: number, blink: boolean) {
  const sep = Math.max(1, h * 0.01)
  const poleH = h * 0.08
  const lightH = (h - sep * 2 - poleH) / 3

  const blinkVisible = !blink || isBlinkOn()
  const lights = [
    { color: LIGHT_COLORS.red, on: !!(value & 4) && blinkVisible },
    { color: LIGHT_COLORS.yellow, on: !!(value & 2) && blinkVisible },
    { color: LIGHT_COLORS.green, on: !!(value & 1) && blinkVisible }
  ]

  let cy = y

  for (let i = 0; i < 3; i++) {
    const light = lights[i]

    // light band — full width
    ctx.fillStyle = light.on ? light.color.on : light.color.off
    ctx.fillRect(x, cy, w, lightH)

    // ON glow overlay
    if (light.on) {
      const cx = x + w / 2
      const my = cy + lightH / 2
      const grad = ctx.createRadialGradient(cx, my, 0, cx, my, w * 0.6)
      grad.addColorStop(0, '#ffffff88')
      grad.addColorStop(1, '#ffffff00')
      ctx.fillStyle = grad
      ctx.fillRect(x, cy, w, lightH)
    }

    cy += lightH

    // thin separator between lights
    if (i < 2) {
      ctx.fillStyle = RING_COLOR
      ctx.fillRect(x, cy, w, sep)
      cy += sep
    }
  }

  // pole stub
  const poleW = w * 0.3
  ctx.fillStyle = POLE_COLOR
  ctx.fillRect(x + (w - poleW) / 2, cy, poleW, poleH)
}

/* ── 3D 렌더 ── */

export class SignalTower3D extends RealObjectGroup {
  private _lightMeshes: THREE.Mesh[] = []

  build() {
    super.build()

    const { width, height } = this.component.bounds
    const { depth } = this.component.state
    const d = depth || width * 5

    const radius = Math.min(width, height) / 2

    // 경광등 부분: width 기반 고정 크기
    const lightH = radius * 1.8
    const ringH = radius * 0.12
    const capH = radius * 0.25
    const baseH = radius * 0.15

    // 경광등 헤드 총 높이 (등3 + 링4 + 캡)
    const headH = lightH * 3 + ringH * 4 + capH

    // 폴: 전체 depth에서 헤드+베이스를 뺀 나머지
    const poleH = Math.max(radius * 0.5, d - headH - baseH)

    // ── static parts (merged) ──
    const statics: THREE.BufferGeometry[] = []

    // base
    const baseGeo = new THREE.CylinderGeometry(radius * 1.4, radius * 1.4, baseH, 24)
    baseGeo.translate(0, baseH / 2, 0)
    statics.push(baseGeo)

    // pole
    const poleGeo = new THREE.CylinderGeometry(radius * 0.25, radius * 0.25, poleH, 12)
    poleGeo.translate(0, baseH + poleH / 2, 0)
    statics.push(poleGeo)

    let y = baseH + poleH

    // ring between pole and first light
    const ring0Geo = new THREE.CylinderGeometry(radius * 1.05, radius * 1.05, ringH, 24)
    ring0Geo.translate(0, y + ringH / 2, 0)
    statics.push(ring0Geo)
    y += ringH

    // rings between lights
    const lightOrder = ['green', 'yellow', 'red'] as const
    for (let i = 0; i < 3; i++) {
      y += lightH
      if (i < 2) {
        const rGeo = new THREE.CylinderGeometry(radius * 1.05, radius * 1.05, ringH, 24)
        rGeo.translate(0, y + ringH / 2, 0)
        statics.push(rGeo)
        y += ringH
      }
    }

    // cap
    const capGeo = new THREE.CylinderGeometry(radius * 0.5, radius * 1.05, capH, 24)
    capGeo.translate(0, y + capH / 2, 0)
    statics.push(capGeo)

    const mergedGeo = BufferGeometryUtils.mergeGeometries(statics)
    const staticMat = new THREE.MeshStandardMaterial({
      color: BODY_COLOR,
      roughness: 0.6,
      metalness: 0.3
    })
    const staticMesh = new THREE.Mesh(mergedGeo, staticMat)
    staticMesh.castShadow = true
    this.object3d.add(staticMesh)

    // ── light meshes (individual for emissive control) ──
    this._lightMeshes = []
    let ly = baseH + poleH + ringH
    const value = (this.component as unknown as SignalTower).value
    const bitMap = [1, 2, 4] // green, yellow, red

    for (let i = 0; i < 3; i++) {
      const colorKey = lightOrder[i]
      const isOn = !!(value & bitMap[i])

      const lightGeo = new THREE.CylinderGeometry(radius, radius, lightH, 24)
      const lightMat = new THREE.MeshStandardMaterial({
        color: isOn ? 0x000000 : LIGHT_COLORS[colorKey].off,
        emissive: isOn ? new THREE.Color(LIGHT_COLORS[colorKey].on) : new THREE.Color(0x000000),
        emissiveIntensity: isOn ? 1.0 : 0,
        roughness: isOn ? 0.1 : 0.7,
        metalness: 0.1
      })

      const mesh = new THREE.Mesh(lightGeo, lightMat)
      mesh.position.y = ly + lightH / 2
      mesh.castShadow = true
      this.object3d.add(mesh)
      this._lightMeshes.push(mesh)

      ly += lightH + (i < 2 ? ringH : 0)
    }

    // center vertically
    const totalH = baseH + poleH + headH
    this.object3d.children.forEach(child => {
      child.position.y -= totalH / 2
    })
  }

  updateLights(forceOff = false) {
    const value = (this.component as unknown as SignalTower).value
    const lightOrder = ['green', 'yellow', 'red'] as const
    const bitMap = [1, 2, 4] // green, yellow, red

    for (let i = 0; i < this._lightMeshes.length; i++) {
      const mat = this._lightMeshes[i].material as THREE.MeshStandardMaterial
      const colorKey = lightOrder[i]
      const isOn = !!(value & bitMap[i]) && !forceOff

      mat.color.set(isOn ? 0x000000 : LIGHT_COLORS[colorKey].off)
      mat.emissive.set(isOn ? LIGHT_COLORS[colorKey].on : 0x000000)
      mat.emissiveIntensity = isOn ? 1.0 : 0
      mat.roughness = isOn ? 0.1 : 0.7
      mat.needsUpdate = true
    }
  }

  blinkTick() {
    this.updateLights(!isBlinkOn())
  }

  update() {
    super.update()
    this.updateLights()
  }

  updateDimension() {
    this.clear()
    this.build()
  }

  onchange(after: Record<string, unknown>, before: Record<string, unknown>) {
    super.onchange(after, before)

    if ('value' in after) {
      this.updateLights()
    }
  }
}

/* ── Component 클래스 ── */

const MixedShape = ValueHolder(RectPath(Shape))

@sceneComponent('signal-tower')
export class SignalTower extends MixedShape {
  private _blinkRaf: number | null = null
  private _lastBlinkOn: boolean = false
  buildRealObject(): RealObject | undefined {
    return new SignalTower3D(this as any)
  }

  get nature() {
    return NATURE
  }

  async ready() {
    await super.ready()
    if (this.getState('blink')) {
      this._startBlink()
    }
  }

  dispose() {
    this._stopBlink()
    super.dispose()
  }

  private _startBlink() {
    if (this._blinkRaf) return

    const tick = () => {
      const on = isBlinkOn()
      if (on !== this._lastBlinkOn) {
        this._lastBlinkOn = on
        this.invalidate()
        if (this._realObject) {
          ;(this._realObject as SignalTower3D).blinkTick()
        }
      }
      this._blinkRaf = requestAnimationFrame(tick)
    }

    this._blinkRaf = requestAnimationFrame(tick)
  }

  private _stopBlink() {
    if (!this._blinkRaf) return
    cancelAnimationFrame(this._blinkRaf)
    this._blinkRaf = null
    // 정상 점등 상태로 복원
    this.invalidate()
    if (this._realObject) {
      ;(this._realObject as SignalTower3D).updateLights()
    }
  }

  onchange(after: Record<string, unknown>, before: Record<string, unknown>) {
    super.onchange(after, before)

    if ('value' in after || 'blink' in after) {
      this.invalidate()

      if (this.getState('blink')) {
        this._startBlink()
      } else {
        this._stopBlink()
      }
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const { left, top, width, height } = this.bounds

    renderSignalTower(ctx, left, top, width, height, this.value, !!this.getState('blink'))
  }

}
